import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ALERT_CATALOG, ALL_TF } from "../../engine";
import { SYMBOL_META, meta } from "../../lib/symbols";
import { useAlerts } from "../../context/AlertsContext";

const GROUPS = ["Price", "Indicators", "Market Structure", "Strategies", "Felacoo Tick Engine"];
const cls = "mt-1 w-full h-9 px-3 rounded-lg border border-border bg-background text-sm outline-none";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

// Catalog-driven create / edit form. The catalog is the same object the backend validates against.
export default function AlertForm({ initial, editing, onDone }) {
  const { createAlert, updateAlert } = useAlerts();
  const [symbol, setSymbol] = useState(initial?.symbol ?? "1HZ50V");
  const [type, setType] = useState(initial?.kind ?? "price");
  const metaT = useMemo(() => ALERT_CATALOG.find((t) => t.type === type), [type]);
  const [condition, setCondition] = useState(initial?.operator ?? "above");
  const [params, setParams] = useState(initial?.params ?? {});
  const [timeframe, setTimeframe] = useState(initial?.timeframe ?? "1s");
  const [cooldown, setCooldown] = useState(initial?.cooldown_s ?? 60);
  const [repeat, setRepeat] = useState(initial?.repeat ?? "every");
  const [note, setNote] = useState(initial?.note ?? "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!metaT) return;
    if (!metaT.conditions.some((c) => c.id === condition)) setCondition(metaT.conditions[0].id);
    setParams((p) => {
      const next = { ...p };
      metaT.params.forEach((pm) => { if (next[pm.key] == null && pm.default != null) next[pm.key] = pm.default; });
      return next;
    });
  }, [metaT]); // eslint-disable-line

  const submit = async () => {
    for (const pm of metaT.params) {
      const v = params[pm.key];
      if (v == null || v === "" || isNaN(+v)) return toast.error(`Enter ${pm.label.toLowerCase()}`);
    }
    const body = {
      symbol, display: meta(symbol).display, kind: type, operator: condition, timeframe, note: note || null,
      params: Object.fromEntries(Object.entries(params).map(([k, v]) => [k, +v])),
      value: params.value != null ? +params.value : null, cooldown_s: +cooldown, repeat,
    };
    setBusy(true);
    try {
      if (editing) await updateAlert(editing.id, { operator: body.operator, timeframe, params: body.params, value: body.value, cooldown_s: body.cooldown_s, repeat, note: body.note });
      else await createAlert(body);
      toast.success(editing ? "Alert updated" : `Alert armed: ${meta(symbol).display} ${metaT.label}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save alert");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="alert-form">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Symbol">
          <select data-testid="alert-form-symbol" className={cls} value={symbol} disabled={!!editing} onChange={(e) => setSymbol(e.target.value)}>
            {Object.entries(SYMBOL_META).map(([code, m]) => <option key={code} value={code}>{m.display} — {m.name}</option>)}
          </select>
        </Field>
        <Field label="Timeframe">
          <select data-testid="alert-form-tf" className={cls} value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
            {ALL_TF.map((t) => <option key={t.id} value={t.id}>{t.label}{t.kind === "tick" ? " (tick bars)" : ""}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Alert type">
        <select data-testid="alert-form-type" className={cls} value={type} disabled={!!editing} onChange={(e) => setType(e.target.value)}>
          {GROUPS.map((g) => (
            <optgroup key={g} label={g}>
              {ALERT_CATALOG.filter((t) => t.group === g).map((t) => <option key={t.type} value={t.type}>{t.label}</option>)}
            </optgroup>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Condition">
          <select data-testid="alert-form-condition" className={cls} value={condition} onChange={(e) => setCondition(e.target.value)}>
            {metaT.conditions.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </Field>
        {metaT.params.map((pm) => (
          <Field key={pm.key} label={pm.label}>
            <input data-testid={`alert-form-param-${pm.key}`} type="number" step={pm.step ?? "any"} min={pm.min} max={pm.max} className={`${cls} font-mono-nums`}
              value={params[pm.key] ?? ""} onChange={(e) => setParams((p) => ({ ...p, [pm.key]: e.target.value }))} />
          </Field>
        ))}
      </div>
      {metaT.needsTicks && <p className="text-[11px] text-primary/80">Tick-engine alerts need tick-built bars: use 1s–30s or tick timeframes.</p>}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Repeat">
          <select data-testid="alert-form-repeat" className={cls} value={repeat} onChange={(e) => setRepeat(e.target.value)}>
            <option value="every">Every time (with cooldown)</option>
            <option value="once">Once, then disable</option>
          </select>
        </Field>
        <Field label="Cooldown (seconds)">
          <input data-testid="alert-form-cooldown" type="number" min={0} className={`${cls} font-mono-nums`} value={cooldown} onChange={(e) => setCooldown(e.target.value)} />
        </Field>
      </div>
      <Field label="Note (optional)">
        <input data-testid="alert-form-note" className={cls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. London open sweep" />
      </Field>
      <button data-testid="alert-form-submit" disabled={busy} onClick={submit}
        className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground font-bold hover:opacity-90 disabled:opacity-60">
        {editing ? "Save changes" : "Arm alert"}
      </button>
    </div>
  );
}
